import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  calculateMaxFrequencyQuery,
  calculateMaxPaymentsQuery,
  changePaymentSimulationQuery,
  createChangePaymentTermsQuery,
  getPopupDetails,
} from './queries';
import { enumListAsRecordObject, getError } from '@utils/graph-utils';
import {
  ILovsTypes,
  initialErrors,
  IPaymentSimulationTableData,
  IPaymentTermsFormsProps,
  IPaymentTermsPopupFormState,
} from '.';
import {
  frequencyArrayToList,
  getPaymentTermsPopupFormState,
  mapToListingData,
  numToList,
} from './utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { IAbstractRecord } from '@models';
import { IListingData } from '@models/listing';
import { paymentTermsHeaders } from './content';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { KeyOf } from '@utils/helper-utils';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import EnhancedButton from '@components/button/EnhancedButton';
import ListingTable from '@components/table/ListingTable';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { IEnhancedRow } from '@components/table/types';
import DateService from '@services/DateService';
import { usePaymentTermsPopupStyles } from './style';

const PaymentTermsForms: React.FunctionComponent<IPaymentTermsFormsProps> = ({
  generalData,
  currencySymbol,
  isProductionBill,
  onClose,
}) => {
  const { classes } = usePaymentTermsPopupStyles();
  const [pageState, setPageState] = useState<IPaymentTermsPopupFormState>(
    getPaymentTermsPopupFormState()
  );
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] =
    useState<boolean>(true);
  const applyButtonClicked = useRef(false);

  const { data } = useQuery(getPopupDetails, {
    variables: {
      policyId: generalData.policy.id,
      parentBillID: generalData.bill.id,
    },
    skip: !generalData.policy.id || !generalData.bill.id,
  });

  const [lovs, setLovs] = useState<ILovsTypes>({
    paymentTypes: {},
    frequencies: {},
    numOfPayments: {},
  });

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: [],
    pageNumber: 0,
    pageSize: 5,
    totalCount: 0,
  });

  const maxEffectiveFrom = useMemo(() => {
    return DateService.subtractMonths(pageState.values.policyExpiryDate, 1);
  }, [pageState.values.policyExpiryDate]);

  const amountToBeAllocated = useMemo(() => {
    if (!pageState.values.bills) return 0;
    const receivableBills = Object.values(pageState.values.bills).filter(
      (bill) => {
        return bill.accounting_Bills_BillType === 'RECEIVABLE';
      }
    );

    const totalAmount: number = receivableBills.reduce(
      (total: number, bill: IAbstractRecord) => {
        return total + bill.accounting_Bills_OutstandingBalance;
      },
      0
    );

    return totalAmount;
  }, [pageState.values.bills]);

  const [calculateMaxFrequency] = useMutation(calculateMaxFrequencyQuery());
  const [calculateMaxPayments] = useMutation(calculateMaxPaymentsQuery());
  const [changePaymentSimulation] = useMutation(changePaymentSimulationQuery());
  const [createChangePaymentTerms] = useMutation(
    createChangePaymentTermsQuery()
  );

  const loadData = async () => {
    try {
      if (!data) return;

      const paymentTypes = enumListAsRecordObject(
        data.Production_PaymentDivisionTypeList?.enumValues
      );
      setLovs((prev) => ({
        ...prev,
        paymentTypes,
      }));

      const policyDetails =
        data.Production?.entities?.policy?.views?.Production_all?.properties;

      const policyExpiryDate =
        policyDetails?.PolicyExpiryDate ||
        DateService.addMonths(policyDetails?.PolicyIssueDate, 6);

      const effectiveFrom = DateService.isDateBefore(
        new Date(),
        policyDetails.PolicyIssueDate
      )
        ? DateService.formatDateBackend(policyDetails.PolicyIssueDate)
        : DateService.formatDateBackend(new Date());

      setPageState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          policyNumber: policyDetails?.PolicyNumber,
          policyIssuanceDate: isProductionBill
            ? policyDetails?.PolicyIssueDate
            : generalData.amendment.effectiveDate,
          policyExpiryDate: policyExpiryDate,
          paymentType: 'EQUIVALENT',
          effectiveFrom: effectiveFrom,
          bills:
            data.Accounting.queries.GetReceivableAndPayableBillsByParentBillID,
        },
      }));

      loadFrequencies(
        isProductionBill
          ? policyDetails?.PolicyIssueDate
          : generalData.amendment.effectiveDate,
        policyExpiryDate,
        new Date()
      );
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    loadData();
  }, [data]);

  const loadFrequencies = async (
    policyIssuanceDate: string,
    policyExpiryDate: string,
    effectiveFromDate: Date
  ) => {
    try {
      const res = await calculateMaxFrequency({
        variables: {
          policyIssuanceDate: DateService.formatDateBackend(policyIssuanceDate),
          policyExpiryDate: DateService.formatDateBackend(policyExpiryDate),
          effectiveFromDate: DateService.formatDateBackend(effectiveFromDate),
        },
      });

      if (
        isEmpty(res.data.accounting?.actions?.calculateMaxFrequency?.Values)
      ) {
        toast.error(
          <ToastErrorMessage>
            Cannot submit. One or many Due Date is greater than Policy Expiry
            Date minus 1 month
          </ToastErrorMessage>
        );
        return;
      }
      const maxFrequencies = frequencyArrayToList(
        res.data.accounting?.actions?.calculateMaxFrequency?.Values
      );

      setLovs((prev) => ({
        ...prev,
        frequencies: maxFrequencies,
      }));
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const loadPayments = async (selectedFrequency: string) => {
    try {
      const res = await calculateMaxPayments({
        variables: {
          policyIssuanceDate: DateService.formatDateBackend(
            pageState.values.policyIssuanceDate
          ),
          policyExpiryDate: DateService.formatDateBackend(
            pageState.values.policyExpiryDate
          ),
          effectiveFromDate: DateService.formatDateBackend(
            pageState.values.effectiveFrom
          ),
          selectedFrequency: selectedFrequency || pageState.values.frequency,
        },
      });

      const maxPayments: number =
        res.data.accounting?.actions?.calculateMaxPayments?.Value;

      setLovs((prev) => ({
        ...prev,
        numOfPayments: numToList(maxPayments),
      }));
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const initialize = async () => {
    await loadData();
  };

  const validateSimulationForm = (
    values = pageState.values
  ): IPaymentTermsPopupFormState['errors'] => {
    const errors: IPaymentTermsPopupFormState['errors'] =
      cloneDeep(initialErrors);
    if (isEmpty(values.effectiveFrom)) {
      errors.effectiveFrom = 'Required';
    }
    if (values.paymentType === 'EQUIVALENT' && isEmpty(values.frequency)) {
      errors.frequency = 'Required';
    }
    if (isEmpty(values.numOfPayments)) {
      errors.numOfPayments = 'Required';
    }

    return errors;
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleApply = async () => {
    try {
      applyButtonClicked.current = true;
      const errors = validateSimulationForm();
      setPageState((prev) => ({
        ...prev,
        errors,
      }));

      if (Object.values(errors).some((error) => !isEmpty(error))) {
        return;
      }

      setIsSubmitButtonDisabled(false);
      const res = await changePaymentSimulation({
        variables: {
          policyIssuanceDate: DateService.formatDateBackend(
            pageState.values.policyIssuanceDate
          ),
          policyExpiryDate: DateService.formatDateBackend(
            pageState.values.policyExpiryDate
          ),
          effectiveFromDate: DateService.formatDateBackend(
            pageState.values.effectiveFrom
          ),
          selectedFrequency:
            pageState.values.paymentType === 'CUSTOM'
              ? 'Month'
              : pageState.values.frequency,
          paymentType: pageState.values.paymentType,
          noOfPayments: Number(pageState.values.numOfPayments),
          noOfPaymentsList: Array.from(
            { length: Number(pageState.values.numOfPayments) - 1 },
            (_, i) => i + 1
          ),
          amountToBeAllocated: amountToBeAllocated,
          currentPolicyID: generalData.policy.id,
          parentBillID: generalData.bill.id,
        },
      });

      if (isEmpty(res.errors)) {
        const pagedItems = mapToListingData(
          res.data.accounting.actions.changePaymentSimulation.List,
          lovs,
          currencySymbol
        );

        setTableData({
          pagedItems: pagedItems,
          pageNumber: 1,
          pageSize: 5,
          totalCount: pagedItems.length,
        });
      } else {
        if (isEqual(res.errors[0].extensions.code, 'NotAllowedNoOfPayments')) {
          setPageState((prev) => ({
            ...prev,
            errors: {
              ...prev.errors,
              numOfPayments: 'Number of Payments is not in allowed range',
            },
          }));
        } else {
          toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        }
      }
    } catch (error) {
      if (
        isEqual(
          error.graphQLErrors[0].extensions.code,
          'NotAllowedNoOfPayments'
        )
      ) {
        setPageState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            numOfPayments: 'Number of Payments is not in allowed range',
          },
        }));
      }
    } finally {
      applyButtonClicked.current = false;
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitButtonDisabled(true);
      const res = await createChangePaymentTerms({
        variables: {
          policyIssuanceDate: pageState.values.policyIssuanceDate,
          policyExpiryDate: pageState.values.policyExpiryDate,
          effectiveFromDate: pageState.values.effectiveFrom,
          selectedFrequency:
            pageState.values.paymentType === 'CUSTOM'
              ? 'Month'
              : pageState.values.frequency,
          paymentType: pageState.values.paymentType,
          noOfPayments: Number(pageState.values.numOfPayments),
          noOfPaymentsList: Array.from(
            { length: Number(pageState.values.numOfPayments) - 1 },
            (_, i) => i + 1
          ),
          amountToBeAllocated: amountToBeAllocated,
          currentPolicyID: generalData.policy.id,
          parentBillID: generalData.bill.id,
          newReceivableBillsList: Object.values(tableData.pagedItems).map(
            (item: IPaymentSimulationTableData) => ({
              AmountDue: item.amountDue,
              BillNumber: item.billNumber,
              BillStatus: item.status,
              DueDate: DateService.formatDateBackend(item.dueDate),
              Commission: item.commission,
              TotalCommission: item.commission,
              TaxOnCommission: item.taxOnCommission,
              TotalPremium: item.totalPremium,
              OutstandingBalance: item.outstandingAmount,
            })
          ),
        },
        errorPolicy: 'all',
      });

      if (res.errors) {
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        return;
      } else {
        toast.success(
          <ToastSuccessMessage>
            Payment Terms Changed Successfully
          </ToastSuccessMessage>
        );
        onClose(true);
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const handleChange = async (inputName: string, value: any) => {
    setIsSubmitButtonDisabled(true);
    let newValues = cloneDeep(pageState.values);
    let newTouched = cloneDeep(pageState.touched);

    newValues = {
      ...newValues,
      [inputName]: value,
    };

    newTouched = {
      ...newTouched,
      [inputName]: true,
    };

    const newErrors = validateSimulationForm(newValues);

    switch (inputName) {
      case 'effectiveFrom':
        if (pageState.values.paymentType === 'EQUIVALENT') {
          newValues.frequency = '';
          newValues.numOfPayments = '';
        }
        break;

      case 'frequency':
        newValues.numOfPayments = null;
        break;

      case 'paymentType':
        newValues.frequency = null;
        break;

      default:
        break;
    }

    switch (inputName) {
      case 'effectiveFrom':
        if (
          pageState.values.paymentType === 'EQUIVALENT' &&
          isEmpty(newErrors.paymentType)
        ) {
          await loadFrequencies(
            newValues.policyIssuanceDate,
            newValues.policyExpiryDate,
            value
          );
        }
        break;
      case 'frequency':
        if (isEmpty(newErrors.frequency)) {
          await loadPayments(value);
        }
        break;
      default:
        break;
    }

    setPageState({
      values: newValues,
      touched: newTouched,
      errors: newErrors,
    });
  };

  const handleCellValueChange = (
    index: number,
    columnName: string,
    value: unknown
  ) => {
    const newTableData = cloneDeep(tableData);
    newTableData.pagedItems[index][columnName] = value;

    if (columnName === 'amountDue') {
      newTableData.pagedItems[index].outstandingAmount = value;
    }

    setTableData((prev) => ({
      ...prev,
      pagedItems: newTableData.pagedItems,
    }));
  };

  const validateAmountDue = () => {
    const newTableData = cloneDeep(tableData);
    const sumOfAmountDue = Object.values(newTableData.pagedItems).reduce(
      (sum: number, item: IPaymentSimulationTableData) =>
        sum + Number(item.amountDue),
      0
    );

    const error = sumOfAmountDue !== amountToBeAllocated;
    let errorMessage = null;
    const newState = cloneDeep(pageState);
    if (error) {
      if (sumOfAmountDue > amountToBeAllocated) {
        errorMessage =
          'Amount Due cannot be greater than Amount to Be Allocated';
      } else if (sumOfAmountDue < amountToBeAllocated) {
        errorMessage = 'Amount to be allocated is not fully allocated';
      } else {
        errorMessage = null;
      }
    }

    newState.errors.amountDue = errorMessage;

    setIsSubmitButtonDisabled(error);
    setPageState(newState);
  };

  const getFieldError = (
    name: KeyOf<IPaymentTermsPopupFormState['touched']>
  ) => {
    if (applyButtonClicked.current || pageState.touched[name]) {
      return pageState.errors[name];
    }

    return '';
  };

  const dynamicRowMessage = (row: IEnhancedRow): string[] => {
    if (row && row.columns) {
      if (pageState.errors.amountDue) {
        return [pageState.errors.amountDue];
      }
    }
    return [];
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleApply();
        }}
      >
        <div className={classes.fieldRow}>
          <TextInputFormField
            title={
              isProductionBill
                ? 'Policy Issuance Date*'
                : 'Amendment Effective Date*'
            }
            name="issuanceDate"
            value={DateService.formatDate(pageState.values.policyIssuanceDate)}
            onBlur={() => undefined}
            onChange={() => undefined}
            disabled
          />
          <TextInputFormField
            title="Policy Expiry Date*"
            name="PolicyExpiryDate"
            value={DateService.formatDate(pageState.values.policyExpiryDate)}
            onBlur={() => undefined}
            onChange={() => undefined}
            disabled
          />
          <SelectFormField
            title="Payment Type*"
            name="paymentType"
            selectOptions={lovs.paymentTypes}
            onChange={(v) => {
              handleChange('paymentType', v);
            }}
            value={pageState.values.paymentType}
          />
        </div>
        <div className={classes.fieldRow}>
          <DatePickerFormField
            title="Change Effective From*"
            name="effectiveFrom"
            value={pageState.values.effectiveFrom}
            onDateChange={(v) => {
              handleChange('effectiveFrom', DateService.formatDateBackend(v));
            }}
            minDate={new Date(pageState.values.policyIssuanceDate)}
            maxDate={maxEffectiveFrom}
            error={getFieldError('effectiveFrom')}
          />
          {pageState.values.paymentType === 'EQUIVALENT' && (
            <SelectFormField
              title="Frequency*"
              name="frequency"
              placeholder="Select a Frequency"
              error={getFieldError('frequency')}
              selectOptions={lovs.frequencies}
              onChange={(v) => {
                handleChange('frequency', v);
              }}
              value={pageState.values.frequency}
            />
          )}
          <TextInputFormField
            title="No of Payments*"
            name="numOfPayments"
            placeholder="Enter No of Payments"
            value={pageState.values.numOfPayments || ''}
            error={getFieldError('numOfPayments')}
            onBlur={() => undefined}
            onChange={(e) => {
              // validate value to be a whole number using regex
              const regex = /^[0-9]*$/;
              if (regex.test(e.target.value)) {
                handleChange('numOfPayments', e.target.value);
              }
            }}
          />
          {pageState.values.paymentType === 'CUSTOM' && (
            <CurrencyFormField
              title="Amount to be Allocated*"
              name="amountToBeAllocated"
              value={amountToBeAllocated}
              onBlur={() => undefined}
              onChange={() => undefined}
              currencySymbol={currencySymbol}
              disabled
            />
          )}
        </div>
        <div className={classes.buttonContainer}>
          <EnhancedButton type="submit" variant="outlined">
            Apply
          </EnhancedButton>
        </div>
      </form>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <ListingTable
          name="paymentTermsSchedule"
          data={tableData}
          headers={paymentTermsHeaders(pageState)}
          disableSelection
          inline
          handleCellValueChanged={handleCellValueChange}
          handleUpdate={(index, columnName) => {
            if (columnName === 'amountDue') {
              validateAmountDue();
            }
          }}
          dynamicRowMessage={dynamicRowMessage}
          actions={[]}
        />
        <div className={classes.buttonsContainer}>
          <EnhancedButton
            type="submit"
            className={classes.button}
            isPrimary
            disabled={isSubmitButtonDisabled}
          >
            Submit
          </EnhancedButton>
        </div>
      </form>
    </>
  );
};

export default PaymentTermsForms;
